<template>
  <v-form
    ref="form"
    v-model="valid"
    class="business-order-form"
    @input="$emit('valid', $event)"
  >
    <v-row>
      <v-col cols="12" md="4">
        <v-row dense>
          <v-col>
            <v-sheet
              class="pa-4"
              color="grey lighten-4 text-subtitle-1"
              rounded
            >
              <strong class="mr-1">{{ $t('ORDERS.DATE_ENTERED') }}:</strong>
              <span>{{ form.date_entered | dateFormat }}</span>
            </v-sheet>
          </v-col>
        </v-row>
      </v-col>

      <v-col cols="12" md="4">
        <v-row dense>
          <v-col>
            <order-transition-status
              v-model="form.status_id"
              :order-id="form.id"
              name="status_id"
              :action="action"
              :readonly="readonly"
              :disabled="!readonly && !editable"
              :label="`${$t('ORDERS.ORDER_STATUS')} *`"
              :error-messages="errors.status_id"
              @change="updateValue('status_id', $event)"
            />
          </v-col>
        </v-row>
      </v-col>

      <v-col cols="12" md="4">
        <v-row dense>
          <v-col>
            <planning-btn
              v-if="readonly"
              :day="form.date_service_expected"
              :order="form.id"
              :disabled="disabledPlanningButton"
              :error-messages="planningButtonErrorMessages"
              color="primary"
              block
              depressed
            ></planning-btn>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="4">
        <v-row dense>
          <v-col>
            <order-customer-details
              class="pa-4"
              :type="form.type_id"
              :customer="company"
              show-title
              rounded
            />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <v-select
              v-model="form.building_complex_id"
              name="building_complex_id"
              item-value="id"
              item-text="name"
              :items="buildingComplexes"
              :clearable="!readonly"
              :readonly="readonly"
              :rules="rules.building_complex_id"
              :label="$t('BUILDING_COMPLEXES.ITEM')"
              :error-messages="errors.building_complex_id"
              @change="updateValue('building_complex_id', $event)"
            />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col v-if="!readonly && !editable">
            <v-select
              v-model="form.service_id"
              name="service_id"
              item-value="id"
              item-text="title"
              :items="services"
              :readonly="readonly || editable"
              :rules="rules.service_id"
              :label="`${$t('SERVICES.ITEM')} *`"
              :error-messages="errors.service_id"
              @change="setService"
            ></v-select>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <order-service-details
              :service="selectedService"
              class="pa-4"
              rounded
            />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <v-switch
              v-model="form.is_tender"
              name="is_tender"
              :label="$t('ORDERS.TENDER')"
              :readonly="readonly"
              :error="errors.is_tender"
              @change="updateValue('is_tender', $event)"
            ></v-switch>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col>
            <v-row dense>
              <v-col>
                <v-select
                  v-model="form.work_profile_id"
                  name="work_profile_id"
                  item-value="id"
                  item-text="value"
                  :items="contactPersonsList"
                  :readonly="readonly"
                  :label="$t('CONTACT_PERSONS.ITEM')"
                  :error-messages="errors.work_profile_id"
                  @change="updateValue('work_profile_id', $event)"
                ></v-select>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col>
                <v-sheet
                  class="pa-4"
                  color="grey lighten-4 text-subtitle-1"
                  rounded
                >
                  <p class="mb-2">
                    <strong class="mr-1"
                      >{{ $t('PROFILE.WORK_MOBILE_PHONE') }}:</strong
                    >
                    <span>{{ contactPersonMobilePhone }}</span>
                  </p>
                  <p class="mb-0">
                    <strong class="mr-1"
                      >{{ $t('PROFILE.WORK_PHONE') }}:</strong
                    >
                    <span>{{ contactPersonPhone }}</span>
                  </p>
                </v-sheet>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>

      <v-col cols="12" md="4">
        <v-row dense>
          <v-col>
            <h3 class="title text--primary">
              {{ $t('ORDERS.SERVICE_DETAILS') }}:
            </h3>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <money-input
              v-model="form.price"
              name="price"
              :counter="4"
              :counter-value="countNumber"
              :readonly="readonly"
              :rules="rules.price"
              :label="$t('ITEM.PRICE')"
              :error-messages="errors.price"
              @input="updateValue('price', $event)"
            />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <v-text-field
              v-model.number="form.duration"
              name="duration"
              type="number"
              min="0"
              step="0.25"
              :suffix="$t('ITEM.HOURS')"
              :counter="4"
              :counter-value="countNumber"
              :readonly="readonly"
              :rules="rules.duration"
              :label="$t('ORDERS.DURATION')"
              :error-messages="errors.duration"
              @input="updateValue('duration', $event)"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <v-text-field
              v-if="readonly"
              :value="form.date_service_expected | dateFormat"
              :label="$t('ORDERS.DATE_SERVICE_EXPECTED')"
              :error-messages="errors.date_service_expected"
              prepend-inner-icon="mdi-calendar"
              readonly
            ></v-text-field>
            <v-menu
              v-else
              ref="menuDateServiceExpected"
              min-width="290px"
              transition="scale-transition"
              :close-on-content-click="false"
              offset-y
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  autocomplete="nope"
                  prepend-inner-icon="mdi-calendar"
                  :value="form.date_service_expected | dateFormat"
                  :label="$t('ORDERS.DATE_SERVICE_EXPECTED')"
                  :readonly="readonly"
                  :error-messages="errors.date_service_expected"
                  clearable
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="form.date_service_expected = null"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="form.date_service_expected"
                :min="minDateServiceExpected"
                first-day-of-week="1"
                show-week
                @change="setDateServiceExpected"
              ></v-date-picker>
            </v-menu>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <v-select
              v-model="form.substitution_options"
              name="substitution_options"
              item-value="id"
              item-text="name"
              :items="substitutionOptions"
              :readonly="readonly"
              :label="$t('ORDERS.SUBSTITUTION_OPTIONS')"
              multiple
              small-chips
              :error-messages="errors.substitution_options"
              @change="updateValue('substitution_options', $event)"
            ></v-select>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <v-textarea
              v-model="form.description"
              name="description"
              :counter="500"
              :readonly="readonly"
              :rules="rules.description"
              :label="$t('ITEM.DESCRIPTION')"
              class="min-height"
              :error-messages="errors.description"
              outlined
              @input="updateValue('description', $event)"
            ></v-textarea>
          </v-col>
        </v-row>
      </v-col>
      <v-divider vertical />
      <v-col cols="12" md="4">
        <v-row dense>
          <v-col>
            <h3 class="title text--primary">
              {{ $t('ORDERS.SERVICE_ADDRESS') }}:
            </h3>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <v-text-field
              v-model="form.street"
              name="street"
              :counter="100"
              :rules="rules.street"
              :readonly="readonly"
              :label="`${$t('PROFILE.STREET')} *`"
              :error-messages="errors.street"
              @change="updateValue('street', $event)"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <zip-code-input
              v-model="form.zip"
              :readonly="readonly"
              :error-messages="errors.zip"
              :rules="rules.zip"
              :label="`${$t('PROFILE.ZIP')} *`"
              @input="updateValue('zip', $event)"
            />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <v-text-field
              v-model="form.city"
              name="city"
              :counter="40"
              :readonly="readonly"
              :rules="rules.city"
              :label="`${$t('PROFILE.CITY')} *`"
              :error-messages="errors.city"
              @change="updateValue('city', $event)"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <v-text-field
              v-model="form.apartment"
              name="apartment"
              :counter="40"
              :readonly="readonly"
              :rules="rules.apartment"
              :label="$t('APARTMENTS.ITEM')"
              :error-messages="errors.apartment"
              @input="updateValue('apartment', $event)"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <order-region
              v-model="form.region_id"
              :zip="form.zip"
              :readonly="readonly"
              :error-messages="errors.region_id"
              @input="updateValue('region_id', $event)"
            />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <v-textarea
              v-model="form.key_deposited"
              name="key_deposited"
              :counter="100"
              :readonly="readonly"
              :rules="rules.key_deposited"
              :label="$t('ORDERS.KEY_DEPOSITED')"
              :error-messages="errors.key_deposited"
              outlined
              @input="updateValue('key_deposited', $event)"
            ></v-textarea>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-row>
      <slot name="buttons" :valid="valid" />
    </v-row>
  </v-form>
</template>
<script>
import { mapActions } from 'vuex';
import { dateFormat } from '@/modules/_global/filters';
import formEditable from '@/modules/_global/mixins/form-editable';
import i18nRefreshData from '@/modules/_global/mixins/i18nRefreshData';
import { BUSINESS } from './../../common/order-types';
import orderForm from './mixins/orderForm';
import { mapCacheActions } from 'vuex-cache';
import OrderRegion from '@/modules/orders/components/forms/OrderRegion.vue';
import ZipCodeInput from '@/modules/_global/_components/zip/ZipCodeInput';
import MoneyInput from '@/modules/_global/_components/money/MoneyInput.vue';
import OrderTransitionStatus from '@/modules/orders/components/forms/OrderTransitionStatus.vue';

export default {
  name: 'OrderBusinessForm',

  filters: {
    dateFormat
  },

  components: {
    OrderTransitionStatus,
    MoneyInput,
    ZipCodeInput,
    PlanningBtn: () => import('@/modules/plannings/components/PlanningButton'),
    OrderRegion
  },

  mixins: [orderForm, formEditable, i18nRefreshData],

  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    company: {
      type: Object,
      default: () => ({
        name: '',
        phone_1: '',
        phone_2: '',
        email: '',
        category: ''
      })
    },
    errors: {
      type: Object,
      default: () => ({})
    },
    readonly: {
      type: Boolean,
      default: false
    },
    editable: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      form: {
        order_number: '',
        date_entered: '',
        street: '',
        zip: '',
        city: '',
        apartment: '',
        key_deposited: '',
        price: '',
        description: '',
        sub_questions: '',
        duration: '',
        substitution_options: null,
        date_service_expected: '',
        is_tender: true,
        service_id: null,
        status_id: 2,
        type_id: BUSINESS,
        work_profile_id: null,
        building_complex_id: null,
        region_id: null
      },
      substitutionOptions: [],
      contactPersons: [],
      buildingComplexes: []
    };
  },

  computed: {
    contactPersonsList() {
      return this.contactPersons.map(person => {
        return {
          id: person.id,
          value: `${person.person.first_name} ${person.person.last_name}`
        };
      });
    },

    contactPerson() {
      return this.contactPersons.find(i => i.id === this.form.work_profile_id);
    },

    contactPersonPhone() {
      const person = this.contactPerson;

      if (!person) {
        return '';
      }

      return person.phone;
    },

    contactPersonMobilePhone() {
      const person = this.contactPerson;

      if (!person) {
        return '';
      }

      return person.mobile_phone;
    }
  },

  watch: {
    company: {
      deep: true,
      immediate: true,
      handler: function(company) {
        if (company.id) {
          this.getWorkProfile({ companyId: this.company.id }).then(
            ({ data }) => {
              this.contactPersons = data.data;
            }
          );
        }
      }
    }
  },

  mounted() {
    this.refreshData();
  },

  methods: {
    ...mapActions('order', ['getWorkProfile']),
    ...mapCacheActions('order', ['getOrderSubstitutionOptions']),
    ...mapActions('service', ['retrieveServicesBCustomer']),
    ...mapActions('building_complex', ['retrieveBuildingComplexesList']),
    refreshData() {
      this.retrieveBuildingComplexesList().then(({ data }) => {
        this.buildingComplexes = data.data;
      });

      this.getOrderSubstitutionOptions().then(({ data }) => {
        this.substitutionOptions = data.data;
      });

      this.retrieveServicesBCustomer().then(({ data }) => {
        this.services = data;
      });
    }
  }
};
</script>

<style lang="scss">
.business-order-form {
  .row {
    > .col,
    > [class*='col-'] {
      padding-top: 4px;
      padding-bottom: 4px;
    }
  }
  .min-height .v-input__slot {
    min-height: 218px;
  }
}
</style>
